import type { ChainKey, DefuseChainId } from '@infinex/chain-config';
import {
  getActiveChains,
  NEAR_INTENTS_CHAIN_METADATA,
} from '@infinex/chain-config';

export interface ChainMetadata {
  name: string;
  blockExplorer: string;
}

// Special type to cover cases where I need to get info about bitcoin etc.
export type MetadataChainKey = ChainKey | DefuseChainId | FrontendChainKey; // TODO: @BEN Figure out a better name for this...

/**
 * Created because we now have a `hyperliquid` chain key on the frontend which isn't an actual chain in chain config
 *
 * (Because we replaced the HyperEVM 4337 deposit address, which was bricking user funds, with the Hyperliquid 7702 one)
 */
export type FrontendChainKey = 'hyperliquid';

export function isFrontendChainKey(
  chainKey: MetadataChainKey
): chainKey is FrontendChainKey {
  return chainKey === 'hyperliquid';
}

/**
 * Creates a cached version of a function that computes a value.
 * The function will only be called once, and subsequent calls return the cached result.
 */
function createCachedGetter<T>(computeFn: () => T): () => T {
  let cached: T | undefined;
  let hasComputed = false;

  return () => {
    if (!hasComputed) {
      cached = computeFn();
      hasComputed = true;
    }
    return cached!;
  };
}

/**
 * Returns metadata for all chains including defuse chains.
 * Lazily computed to avoid side effects at module load time.
 */
export const getChainMetadataMap = createCachedGetter<
  Record<MetadataChainKey, ChainMetadata>
>(() => {
  // Build metadata from active chains
  const envChainMetadataMap = Object.entries(getActiveChains()).reduce(
    (acc, [chainKey, config]) => ({
      ...acc,
      [chainKey]: {
        name: config.displayName,
        blockExplorer: config.connectivity.blockExplorers.primary.base,
      },
    }),
    {} as Record<ChainKey, ChainMetadata>
  );

  // Build defuse chain metadata from NEAR_INTENTS_CHAIN_METADATA
  const nearIntentsChainMetadataMap = Object.entries(
    NEAR_INTENTS_CHAIN_METADATA
  ).reduce(
    (acc, [chainId, config]) => ({
      ...acc,
      [chainId]: {
        name: config.name,
        blockExplorer: config.blockExplorer,
      },
    }),
    {} as Record<DefuseChainId, ChainMetadata>
  );

  // Merge with defuse chain metadata
  return {
    ...envChainMetadataMap,
    ...nearIntentsChainMetadataMap,
    hyperliquid: {
      name: 'Hyperliquid',
      blockExplorer: 'https://hypurrscan.io',
    },
  } as const;
});

export const getChainMetadata = (chainKey: MetadataChainKey): ChainMetadata => {
  return getChainMetadataMap()[chainKey];
};

/**
 * Returns color map for all chains including defuse chains.
 * Lazily computed to avoid side effects at module load time.
 */
export const getChainColorMap = createCachedGetter<
  Record<MetadataChainKey, string>
>(() => {
  // Build colors from active chains
  const envChainColorMap = Object.entries(getActiveChains()).reduce(
    (acc, [chainKey, config]) => ({
      ...acc,
      [chainKey]: config.decoration.color,
    }),
    {} as Record<ChainKey, string>
  );

  // Build defuse chain colors from NEAR_INTENTS_CHAIN_METADATA
  const nearIntentsChainColorMap = Object.entries(
    NEAR_INTENTS_CHAIN_METADATA
  ).reduce(
    (acc, [chainId, config]) => ({
      ...acc,
      [chainId]: config.decoration.color,
    }),
    {} as Record<DefuseChainId, string>
  );

  // Merge with defuse chain colors
  return {
    ...envChainColorMap,
    ...nearIntentsChainColorMap,
    hyperliquid: envChainColorMap['hyperEVM'],
  } as const;
});

export const getChainColor = (chainKey: MetadataChainKey): string => {
  return getChainColorMap()[chainKey];
};
